/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/

import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { UserApiService } from '../../services/UserApiService';
import { RootState } from '../../store';
import { authRefreshUser } from '../../store/auth/actions';
import { INotification } from '../../store/notifications/types';
import { EntityId } from '../../types/api/EntityId';
import { AppServerEvents } from '../../types/common/AppEvents';
import { DataState } from '../../types/common/DataState';
import { NotificationItemFrameOptions } from '../../types/notification/NotificationItemFrameOptions';
import { dataLoaded, dataLoading, isDataLoading } from '../../utils/dataState';
import { reactToResolveOrganizationInvitation } from '../../utils/reactions';
import { useNotificationRelativeDate } from '../../utils/useNotificationRelativeDate';
import { NotificationItemFrame } from '../NotificationItemFrame/NotificationItemFrame';

const mapState = (state: RootState) => ({ appUser: state.auth.user });
const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type OrganizationInvitationNotificationProps = NotificationItemFrameOptions &
  PropsFromRedux & {
    notification: INotification<AppServerEvents.OrganizationInvitation>;
  };

const OrganizationMembershipNotification = memo<OrganizationInvitationNotificationProps>(props => {
  const {
    notification,
    setNotificationAsRead,
    asSnackbar,
    onRemoveBtnClick,
    appUser,
    onFirstButtonClick,
    onSecondButtonClick,
    onRootClick,
  } = props;
  const { createdAt, event, isRead } = notification;
  const { organization, user, invitationId } = event;
  const [dataState, setDataState] = useState<DataState>(DataState.loaded);
  const [resolved, setResolved] = useState<boolean>(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation([Namespaces.Navigation]);

  const invitationExists = useMemo<boolean>(() => {
    if (!appUser) {
      return false;
    }
    return !!appUser.invitations.find(i => i.id === invitationId);
  }, [appUser, invitationId]);

  const resolveInvite = async (inviteId: EntityId, accept: boolean) => {
    setDataState(dataLoading());
    try {
      await UserApiService.resolveOrganizationInvitation({
        invitationId: inviteId,
        accept,
      });
      dispatch(authRefreshUser());
    } catch (e) {
      /* empty */
    } finally {
      setDataState(dataLoaded());
      setResolved(true);
      void reactToResolveOrganizationInvitation({ organizationId: organization.id });
    }
  };

  const acceptInvite = () => {
    onFirstButtonClick && onFirstButtonClick();
    void resolveInvite(invitationId, true);
  };

  const declineInvite = () => {
    onSecondButtonClick && onSecondButtonClick();
    void resolveInvite(invitationId, false);
  };

  const [datetime] = useNotificationRelativeDate(createdAt);

  const isResolving = isDataLoading(dataState);

  const actionsParams =
    invitationExists && !resolved
      ? {
          firstButtonText: t('notifications.labels.accept'),
          onFirstButtonClick: isResolving ? undefined : acceptInvite,
          secondButtonText: t('notifications.labels.decline'),
          onSecondButtonClick: isResolving ? undefined : declineInvite,
        }
      : {};

  const onClick = () => {
    onRootClick && onRootClick({ id: notification.id });
    invitationExists && void navigate(BasicRoutes.ORGANIZATIONS);
  };

  const inviterUserName = useMemo(
    () =>
      [user.firstName, user.lastName]
        .filter(i => !!i)
        .join(' ')
        .trim(),
    [user.firstName, user.lastName],
  );

  return (
    <NotificationItemFrame
      datetime={datetime}
      setNotificationAsRead={setNotificationAsRead}
      isRead={asSnackbar ? true : isRead}
      primaryText={
        inviterUserName
          ? t('notifications.templates.user_invited_you_to_join', {
              user: inviterUserName,
            })
          : t('notifications.templates.you_invited_to_organization', {
              organizationName: organization?.name,
            })
      }
      secondaryText={organization?.name}
      id={notification.id}
      asSnackbar={asSnackbar}
      onRemoveBtnClick={onRemoveBtnClick}
      onRootClick={onClick}
      {...actionsParams}
    />
  );
});

export const OrganizationInvitationNotification = connector(OrganizationMembershipNotification);
